import { ClientEvents } from 'common/events/ClientEvents';
import { isNil, noop, size } from 'lodash';
import React, { useCallback, useState } from 'react';
import {
  ConsumerGroupOrderFieldsFragment,
  OrderType,
  useConsumerCancelOrderMutation,
} from 'src/gqlReactTypings.generated.d';
import { ButtonColors } from 'src/shared/design-system/Buttons';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Notifications } from 'src/shared/Notifications';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { Colors } from 'web-common/src/shared/styles';
import { OrdersListRowButton } from './styles';

interface Props {
  groupOrder: ConsumerGroupOrderFieldsFragment;
}

const SkipButtonStyled = styled(OrdersListRowButton).attrs(() => ({ color: ButtonColors.WHITE }))`
  color: ${ifProp('disabled', Colors.GRAY_DARK_10, Colors.BLACK)};
  border: 2px solid ${ifProp('disabled', Colors.GRAY_DARK_10, Colors.RADISH)};
`;

export const SkipButton: React.FC<Props> = ({ groupOrder: { id, orders } }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [skipOrderMutation] = useConsumerCancelOrderMutation();
  const tracker = useTracker();

  const doSkip = useCallback(async () => {
    setIsLoading(true);

    const { errors } = await skipOrderMutation({
      variables: {
        groupOrderId: id,
      },
    });

    tracker.track(ClientEvents.RECURRING_SUBSCRIPTION_SKIP_ORDER_BUTTON_CLICK, { groupOrderId: id });

    if (errors) {
      Notifications.error('We were unable to skip your order. Please contact support');
      console.error('Failed to Skip Order.', errors);
    } else {
      Notifications.success('Subscription order successfully skipped!');
      setDisabled(true);
    }
    setIsLoading(false);
  }, [id, skipOrderMutation, tracker]);

  if (
    size(orders) > 1 ||
    orders[0].orderType !== OrderType.Recurring ||
    isNil(orders[0].subscriptionInvoiceId) ||
    !orders[0].isCancelable
  ) {
    return null;
  }

  const text = match({ isLoading, disabled })
    .with({ isLoading: false, disabled: false }, () => 'Skip Order')
    .with({ isLoading: false, disabled: true }, () => 'Skipped')
    .otherwise(() => 'Loading');

  return (
    <SkipButtonStyled onClick={isLoading ? noop : doSkip} disabled={disabled}>
      {text}
    </SkipButtonStyled>
  );
};
